import { useMemo, useState, type ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  CohortBoardPayload,
  MapDrillStone,
  MapInvestigationLine,
  MapInvestigationProbe,
  MapSegmentTile,
} from '@/components/CohortBoard';

type Props = {
  board: CohortBoardPayload;
  memoSlot?: ReactNode;
  onInvestigate?: (ask: string) => void;
};

type SegmentRow = {
  name: string;
  q2: number;
  pct: number;
  color: string;
  note: string;
};

type CustomerRow = {
  label: string;
  pct: number;
  usd: number;
  segment: string;
  color: string;
  note: string;
};

type KpiRow = {
  label: string;
  value: string;
  delta: string;
  up: boolean;
};

type CapexRow = {
  name: string;
  value: number;
  color: string;
};

const SPCX_SEC_10Q =
  'https://www.sec.gov/Archives/edgar/data/1181412/000162828026052535/spcx-20260630.htm';

const SPCX_SEGMENT_STYLE: Record<string, { color: string; note: string }> = {
  'Starlink Consumer': {
    color: '#0f3460',
    note: '12.0M subscribers · kit + monthly broadband',
  },
  'AI Solutions & Infrastructure': {
    color: '#1a1a2e',
    note: 'CSAs · 1.4 GW nameplate · Colossus',
  },
  'Enterprise & Government': {
    color: '#16213e',
    note: 'Aviation · maritime · Starshield · Mobile',
  },
  'Customer launch services': {
    color: '#533483',
    note: '10 customer launches · $64.8M blended',
  },
  'X advertising': {
    color: '#7b2d8b',
    note: 'Platform ads · down YoY from $426M',
  },
  'Launch & development': {
    color: '#c2294a',
    note: 'NASA HLS · CCTCap · USDV cost-to-cost',
  },
};

const SPCX_KPIS: KpiRow[] = [
  { label: 'Q2 Revenue', value: '$7.81B', delta: 'est. +~2× YoY', up: true },
  {
    label: 'Adj. EBITDA',
    value: '$3.54B',
    delta: 'D&A $2.85B + SBC $831M add-back',
    up: true,
  },
  { label: 'Starlink Subs', value: '12.0M', delta: '+1.7M vs Q1', up: true },
  { label: 'AI Nameplate', value: '1.4 GW', delta: '+0.4 GW vs Q1', up: true },
];

const SPCX_TREND = [
  { month: "Q1 '25", rev: 2.1 },
  { month: "Q2 '25", rev: 2.9 },
  { month: "Q3 '25", rev: 3.8 },
  { month: "Q4 '25", rev: 4.7 },
  { month: "Q1 '26", rev: 6.1 },
  { month: "Q2 '26", rev: 7.81 },
];

const SPCX_SEGMENT_OPS = [
  { name: 'Connectivity', ops: 1656, color: '#0f3460' },
  { name: 'Space', ops: -542, color: '#533483' },
  { name: 'AI S&I', ops: -1257, color: '#c2294a' },
];

const SPCX_CAPEX_COLORS: Record<string, string> = {
  'H1 AI capex': '#1a1a2e',
  'H1 Connectivity capex': '#0f3460',
  'H1 Space capex': '#533483',
};

const SPCX_BACKLOG = [
  { label: 'Backlog', value: '$47.5B', sub: '56% within 1 year' },
  { label: 'Deferred Revenue', value: '$14.3B', sub: '$7.98B current · $6.31B LT' },
  {
    label: 'AI Contracted Sales',
    value: '$14.1B',
    sub: 'Non-cancellable window · not backlog',
  },
  {
    label: 'Cash + Marketable Sec.',
    value: '$100B',
    sub: 'IPO $85.7B + June notes $25B',
  },
];

const SPCX_OPEN_FLAGS = [
  'Starlink ARPU $66 × 12M subscribers ≠ Consumer $2,485M — identity does not close',
  'Customer A and Customer B legal names unprinted in 10-Q Note 3',
  'Utilized MW vs 1.4 GW nameplate unprinted — $/kW-month unknown',
  'SAM FA254126FB015 Warrior Plans Starshield TO — dollars blank on SAM and USAspending',
  '65 MHz sits in Spectrum Business Trust 2025-1 until ~Nov 30, 2027 — not SpaceX license yet',
  'Grok/X subscription dollars mixed inside AI S&I $2,194M',
];

const SEGMENT_COLORS = ['#0f3460', '#1a1a2e', '#16213e', '#533483', '#7b2d8b', '#c2294a'];

function fmtM(n: number) {
  return Math.abs(n) >= 1000
    ? `${n < 0 ? '−' : ''}$${(Math.abs(n) / 1000).toFixed(1)}B`
    : `${n < 0 ? '−' : ''}$${Math.abs(n).toFixed(0)}M`;
}

function chartUnitScale(unit?: string) {
  const u = (unit || '').toLowerCase();
  if (u.includes('million')) return 1;
  if (u.includes('billion') || u === '$b') return 1000;
  return 1;
}

function segmentsFromBoard(board: CohortBoardPayload): SegmentRow[] {
  let raw: MapSegmentTile[] = [];
  if (board.revenue_segments?.length) {
    raw = board.revenue_segments;
  } else {
    const pie = board.charts?.find((c) => c.kind === 'pie');
    if (!pie?.data?.length) return [];
    const total = pie.data.reduce((sum, row) => sum + row.value, 0);
    raw = pie.data.map((row, i) => ({
      name: row.name,
      value: row.value,
      pct: total > 0 ? Math.round((row.value / total) * 1000) / 10 : 0,
      color: SEGMENT_COLORS[i % SEGMENT_COLORS.length],
    }));
  }

  const nodeNotes = new Map<string, string>();
  board.businesses?.nodes?.forEach((node) => {
    if (node.sub) nodeNotes.set(node.title.trim(), node.sub);
  });

  return raw.map((row, i) => {
    const style = SPCX_SEGMENT_STYLE[row.name];
    const nodeNote = [...nodeNotes.entries()].find(([title]) =>
      row.name.toLowerCase().startsWith(title.slice(0, 12).toLowerCase()),
    )?.[1];
    return {
      name: row.name,
      q2: row.value,
      pct: row.pct,
      color: style?.color ?? row.color ?? SEGMENT_COLORS[i % SEGMENT_COLORS.length],
      note: row.note ?? style?.note ?? nodeNote?.slice(0, 72) ?? '',
    };
  });
}

function customersFromBoard(board: CohortBoardPayload, totalQ2: number): CustomerRow[] {
  const bubbles = board.payer_bubbles ?? [];
  const customerB = bubbles.find((p) => p.label === 'Customer B');
  const customerA = bubbles.find((p) => p.label === 'Customer A');

  const bPct = customerB?.pct ?? 19.5;
  const aPct = customerA?.pct ?? 18.3;
  const bUsd = customerB ? customerB.rev * 1000 : Math.round((bPct / 100) * totalQ2);
  const aUsd = customerA ? customerA.rev * 1000 : Math.round((aPct / 100) * totalQ2);
  const otherPct = Math.round((100 - bPct - aPct) * 10) / 10;
  const otherUsd = Math.max(0, totalQ2 - bUsd - aUsd);

  return [
    {
      label: 'Customer B',
      pct: bPct,
      usd: bUsd,
      segment: 'AI only',
      color: '#1a1a2e',
      note: 'Google / Anthropic named on call — not assigned in 10-Q',
    },
    {
      label: 'Customer A',
      pct: aPct,
      usd: aUsd,
      segment: 'Space + Connectivity + AI',
      color: '#0f3460',
      note: 'Not identified as NASA or DoD in Note 3',
    },
    {
      label: 'All others',
      pct: otherPct,
      usd: otherUsd,
      segment: 'All segments',
      color: '#e5e5e5',
      note: '',
    },
  ];
}

function kpisFromBoard(board: CohortBoardPayload): KpiRow[] {
  if (board.object?.toUpperCase() === 'SPCX') return SPCX_KPIS;
  if (!board.stats?.length) return [];
  return board.stats.slice(0, 4).map((stat) => ({
    label: stat.label.slice(0, 24),
    value: stat.value.split(' ')[0] ?? stat.value,
    delta: stat.value.includes(' ') ? stat.value.slice(stat.value.indexOf(' ') + 1) : '',
    up: stat.tone !== 'danger',
  }));
}

function capexFromBoard(board: CohortBoardPayload): { rows: CapexRow[]; ocf: number } {
  const bar = board.charts?.find((c) => c.kind === 'bar');
  if (!bar?.data?.length) {
    if (board.object?.toUpperCase() === 'SPCX') {
      return {
        rows: [
          { name: 'AI Infrastructure', value: 23551, color: '#1a1a2e' },
          { name: 'Connectivity (Starlink)', value: 2699, color: '#0f3460' },
          { name: 'Space', value: 2226, color: '#533483' },
        ],
        ocf: 3466,
      };
    }
    return { rows: [], ocf: 0 };
  }

  const scale = chartUnitScale(bar.unit);
  let ocf = 0;
  const rows: CapexRow[] = [];
  bar.data.forEach((row) => {
    const val = row.value * scale;
    if (/ocf/i.test(row.name)) {
      ocf = val;
      return;
    }
    if (!/capex/i.test(row.name)) return;
    const shortName = row.name
      .replace(/^H1\s+/i, '')
      .replace(/\s+capex$/i, '')
      .replace(/^AI$/i, 'AI Infrastructure')
      .replace(/^Connectivity$/i, 'Connectivity (Starlink)');
    rows.push({
      name: shortName,
      value: val,
      color: SPCX_CAPEX_COLORS[row.name] ?? '#533483',
    });
  });
  return { rows, ocf };
}

function openFlagsFromBoard(board: CohortBoardPayload): string[] {
  if (board.object?.toUpperCase() === 'SPCX') return SPCX_OPEN_FLAGS;
  const flags: string[] = [];
  board.overreach?.forEach((item) => {
    flags.push(item.because ? `${item.claim} — ${item.because}` : item.claim);
  });
  if (board.watch) flags.push(board.watch);
  return flags;
}

function headerSubtitle(board: CohortBoardPayload) {
  if (board.object?.toUpperCase() === 'SPCX') {
    return 'Q2 2026 · Three-segment · 10-Q filed Aug 4 2026 · EIN 01-0627671';
  }
  const parts = [board.as_of ? `As of ${board.as_of}` : null].filter(Boolean);
  return parts.join(' · ') || '';
}

function KPICard({ label, value, delta, up }: KpiRow) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-[#e5e5e5] bg-white px-5 py-4 transition-colors hover:border-[#ccc]">
      <span className="text-[10px] font-medium uppercase tracking-widest text-[#999]">
        {label}
      </span>
      <span className="text-[22px] leading-tight text-[#0d0d0d]">{value}</span>
      {delta ? (
        <span className={`text-[11px] font-medium ${up ? 'text-emerald-600' : 'text-rose-500'}`}>
          {up ? '↑' : '↓'} {delta}
        </span>
      ) : null}
    </div>
  );
}

function SegmentBar({
  segments,
  selected,
  onSelect,
}: {
  segments: SegmentRow[];
  selected?: string | null;
  onSelect?: (name: string) => void;
}) {
  const [hov, setHov] = useState<string | null>(null);
  const active = hov || selected;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-9 gap-0.5 overflow-hidden rounded-lg">
        {segments.map((s) => (
          <button
            type="button"
            key={s.name}
            className="relative flex cursor-pointer items-center justify-center transition-opacity"
            style={{
              width: `${s.pct}%`,
              backgroundColor: s.color,
              opacity: active && active !== s.name ? 0.4 : 1,
            }}
            onMouseEnter={() => setHov(s.name)}
            onMouseLeave={() => setHov(null)}
            onClick={() => onSelect?.(s.name)}
          >
            {s.pct > 8 ? (
              <span className="select-none font-mono text-[9px] font-semibold tracking-tight text-white/80">
                {s.pct}%
              </span>
            ) : null}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
        {segments.map((s) => (
          <button
            type="button"
            key={s.name}
            className={`flex cursor-pointer items-start gap-2 text-left transition-opacity ${
              active && active !== s.name ? 'opacity-30' : 'opacity-100'
            }`}
            onMouseEnter={() => setHov(s.name)}
            onMouseLeave={() => setHov(null)}
            onClick={() => onSelect?.(s.name)}
          >
            <div
              className="mt-[3px] h-2 w-2 shrink-0 rounded-sm"
              style={{ backgroundColor: s.color }}
            />
            <div className="min-w-0">
              <div className="truncate text-[11px] font-semibold text-[#0d0d0d]">{s.name}</div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-[#555]">{fmtM(s.q2)}</span>
                {s.note ? (
                  <>
                    <span className="text-[10px] text-[#bbb]">·</span>
                    <span className="truncate text-[10px] text-[#999]">{s.note}</span>
                  </>
                ) : null}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function CustomerConc({ customers }: { customers: CustomerRow[] }) {
  const [hov, setHov] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-6 gap-0.5 overflow-hidden rounded-lg">
        {customers.map((c) => (
          <div
            key={c.label}
            className="relative flex cursor-pointer items-center justify-center transition-opacity"
            style={{
              width: `${c.pct}%`,
              backgroundColor: c.color,
              opacity: hov && hov !== c.label ? 0.3 : 1,
            }}
            onMouseEnter={() => setHov(c.label)}
            onMouseLeave={() => setHov(null)}
          >
            {c.pct > 15 ? (
              <span className="font-mono text-[9px] text-white/80">{c.label}</span>
            ) : null}
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {customers.slice(0, 2).map((c) => (
          <div
            key={c.label}
            className={`flex cursor-default items-start gap-2.5 rounded-lg border p-2.5 transition-all ${
              hov === c.label ? 'border-[#ccc] bg-white' : 'border-[#f0f0f0] bg-[#fafafa]'
            }`}
            onMouseEnter={() => setHov(c.label)}
            onMouseLeave={() => setHov(null)}
          >
            <div
              className="mt-1 h-2 w-2 shrink-0 rounded-sm"
              style={{ backgroundColor: c.color }}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[12px] font-semibold text-[#0d0d0d]">{c.label}</span>
                <span className="font-mono text-[11px] text-[#0d0d0d]">~{fmtM(c.usd)}</span>
              </div>
              <div className="mt-0.5 text-[10px] text-[#999]">
                {c.pct}% of Q2 · {c.segment}
              </div>
              {c.note ? (
                <div className="mt-0.5 text-[10px] italic text-[#bbb]">{c.note}</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ValueTooltip({
  active,
  payload,
  label,
  suffix = 'M',
}: {
  active?: boolean;
  payload?: Array<{ value?: number | string | null }>;
  label?: string;
  suffix?: string;
}) {
  if (!active || !payload?.length) return null;
  const v = Number(payload[0].value);
  if (!Number.isFinite(v)) return null;
  return (
    <div className="rounded-lg border border-[#e5e5e5] bg-white px-3 py-2 text-[12px] shadow-md">
      <div className="font-semibold text-[#0d0d0d]">{label}</div>
      <div className="font-mono text-[#555]">
        {suffix === 'B' ? `$${v}B Q revenue` : `${v < 0 ? '−' : ''}$${Math.abs(v).toLocaleString()}M`}
      </div>
    </div>
  );
}

function CapexBar({
  rows,
  ocf,
  showFundingNote,
}: {
  rows: CapexRow[];
  ocf: number;
  showFundingNote?: boolean;
}) {
  const total = rows.reduce((a, b) => a + b.value, 0);
  if (!total) return null;
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex h-4 gap-0.5 overflow-hidden rounded-md">
        {rows.map((c) => (
          <div
            key={c.name}
            style={{ width: `${(c.value / total) * 100}%`, backgroundColor: c.color }}
          />
        ))}
      </div>
      {rows.map((c) => (
        <div key={c.name} className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-2 w-2 rounded-sm" style={{ backgroundColor: c.color }} />
            <span className="text-[11px] text-[#555]">{c.name}</span>
          </div>
          <span className="font-mono text-[11px] text-[#0d0d0d]">{fmtM(c.value)}</span>
        </div>
      ))}
      <div className="mt-0.5 flex items-center justify-between border-t border-[#f0f0f0] pt-2">
        <span className="text-[11px] font-semibold text-[#0d0d0d]">H1 total PP&E</span>
        <span className="font-mono text-[11px] font-semibold text-[#0d0d0d]">{fmtM(total)}</span>
      </div>
      {ocf > 0 ? (
        <>
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-[#999]">H1 operating cash flow</span>
            <span className="font-mono text-[11px] text-rose-500">{fmtM(ocf)}</span>
          </div>
          {showFundingNote ? (
            <div className="text-[10px] italic text-[#bbb]">
              IPO ($85.7B) + June notes ($25B) fund the gap
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

const KIND_LABEL: Record<string, string> = {
  compose: 'Units × price',
  mix: 'What the print mixes',
  payer: 'Who pays',
  unused_file: 'Unused file',
  dated_print: 'Dated print',
  cash: 'Cash vs KPI',
  overreach: 'Inference',
};

const STATUS_CLASS: Record<string, string> = {
  closed: 'bg-emerald-50 text-emerald-800',
  unclosed: 'bg-amber-50 text-amber-900',
  conceded: 'bg-amber-50 text-amber-900',
  hole: 'bg-[#f4f4f5] text-[#666]',
  overreach: 'bg-rose-50 text-rose-800',
  open: 'bg-[#f7f7f8] text-[#555]',
};

function matchInvestigationLine(
  lines: MapInvestigationLine[],
  segmentName: string,
): MapInvestigationLine | undefined {
  const exact = lines.find((l) => l.name === segmentName);
  if (exact) return exact;
  const needle = segmentName.toLowerCase().slice(0, 14);
  return lines.find(
    (l) =>
      l.name.toLowerCase().includes(needle) ||
      segmentName.toLowerCase().includes(l.name.toLowerCase().slice(0, 14)),
  );
}

function ProbeCard({
  probe,
  onInvestigate,
}: {
  probe: MapInvestigationProbe;
  onInvestigate?: (ask: string) => void;
}) {
  return (
    <div className="rounded-xl border border-[#e5e5e5] bg-white px-4 py-3">
      <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] font-medium uppercase tracking-widest text-[#999]">
          {KIND_LABEL[probe.kind] ?? probe.kind}
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_CLASS[probe.status] ?? STATUS_CLASS.open}`}
        >
          {probe.status}
        </span>
        <span className="font-mono text-[10px] text-[#bbb]">{probe.constraint_class}</span>
      </div>
      <p className="text-[13px] leading-5 text-[#0d0d0d]">{probe.claim}?</p>
      {probe.already ? (
        <p className="mt-1.5 text-[11px] leading-4 text-[#666]">Map already: {probe.already}</p>
      ) : null}
      {probe.fetch ? (
        <p className="mt-1 text-[11px] leading-4 text-[#888]">Fetch: {probe.fetch}</p>
      ) : null}
      {probe.close_if ? (
        <p className="mt-1 text-[11px] leading-4 text-[#888]">Closes if: {probe.close_if}</p>
      ) : null}
      {probe.finding ? (
        <div className="mt-2 rounded-lg border border-[#eee] bg-[#fafafa] px-3 py-2">
          <div className="text-[10px] font-medium uppercase tracking-widest text-[#999]">
            Warehouse
          </div>
          <p className="mt-1 text-[12px] leading-5 text-[#333]">{shortFinding(probe.finding)}</p>
        </div>
      ) : null}
      {onInvestigate ? (
        <button
          type="button"
          onClick={() => onInvestigate(probe.ask)}
          className="mt-3 rounded-lg bg-[#0d0d0d] px-3 py-1.5 text-[12px] text-white transition-colors hover:bg-[#333]"
        >
          Warehouse ask
        </button>
      ) : null}
    </div>
  );
}

function InvestigationDeck({
  lines,
  howToUse,
  selected,
  onSelect,
  onInvestigate,
}: {
  lines: MapInvestigationLine[];
  howToUse?: string;
  selected: string | null;
  onSelect: (name: string) => void;
  onInvestigate?: (ask: string) => void;
}) {
  const current = lines.find((l) => l.name === selected) ?? lines[0];
  if (!current) return null;
  const looksLikeProduct = (line: MapInvestigationLine) =>
    line.bucket === 'revenue' ||
    line.probes.filter((p) => p.kind === 'compose' || p.kind === 'mix' || p.kind === 'payer').length >= 2;
  const revenueLines = lines.filter(looksLikeProduct);
  const crossLines = lines.filter((l) => !looksLikeProduct(l));
  const primary = revenueLines.length ? revenueLines : lines;
  return (
    <div className="flex flex-col gap-3">
      <div>
        <div className="text-[12px] font-semibold text-[#0d0d0d]">Investigate a line</div>
        <p className="mt-0.5 text-[11px] leading-4 text-[#999]">
          {howToUse ||
            'Each question is a warehouse identity — unused file, units × price, dated print. Not a pitch.'}
        </p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {primary.map((line) => (
          <button
            type="button"
            key={line.name}
            onClick={() => onSelect(line.name)}
            className={`rounded-full border px-3 py-1 text-[11px] transition-colors ${
              current.name === line.name
                ? 'border-[#0d0d0d] bg-[#0d0d0d] text-white'
                : 'border-[#e5e5e5] bg-white text-[#555] hover:border-[#ccc]'
            }`}
          >
            {line.name.length > 36 ? `${line.name.slice(0, 34)}…` : line.name}
          </button>
        ))}
      </div>
      {crossLines.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {crossLines.map((line) => (
            <button
              type="button"
              key={line.name}
              onClick={() => onSelect(line.name)}
              className={`rounded-full border px-3 py-1 text-[11px] transition-colors ${
                current.name === line.name
                  ? 'border-[#0d0d0d] bg-[#0d0d0d] text-white'
                  : 'border-[#eee] bg-[#fafafa] text-[#666] hover:border-[#ccc]'
              }`}
            >
              {line.name.length > 40 ? `${line.name.slice(0, 38)}…` : line.name}
            </button>
          ))}
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        {current.probes.map((probe) => (
          <ProbeCard key={probe.id} probe={probe} onInvestigate={onInvestigate} />
        ))}
      </div>
    </div>
  );
}

function shortFinding(text: string): string {
  const paras = text.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  const keep = paras.slice(0, 2).join('\n\n');
  if (keep.length <= 520) return keep;
  return `${keep.slice(0, 500).replace(/\s+\S*$/, '')}…`;
}

function DrillFindings({
  stones,
  drill,
}: {
  stones: MapDrillStone[];
  drill?: string;
}) {
  const counts = stones.reduce<Record<string, number>>((acc, s) => {
    acc[s.status] = (acc[s.status] ?? 0) + 1;
    return acc;
  }, {});
  const summary = (['closed', 'conceded', 'hole', 'unclosed'] as const)
    .filter((k) => counts[k])
    .map((k) => `${counts[k]} ${k}`)
    .join(' · ');
  if (!stones.length && !drill) return null;
  return (
    <div className="flex flex-col gap-3" data-map-drill>
      <div>
        <div className="text-[12px] font-semibold text-[#0d0d0d]">Warehouse findings</div>
        <p className="mt-0.5 text-[11px] leading-4 text-[#999]">
          {summary || 'Close or concede from unused files. Not a pitch.'}
        </p>
      </div>
      {stones.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {stones.map((stone) => (
            <div
              key={stone.title}
              className="rounded-xl border border-[#e5e5e5] bg-white px-4 py-3"
            >
              <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                <span
                  className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_CLASS[stone.status] ?? STATUS_CLASS.open}`}
                >
                  {stone.status}
                </span>
                <span className="text-[13px] font-semibold text-[#0d0d0d]">{stone.title}</span>
              </div>
              {stone.finding ? (
                <p className="whitespace-pre-wrap text-[12px] leading-5 text-[#555]">
                  {shortFinding(stone.finding)}
                </p>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function FlagRow({ flag }: { flag: string }) {
  return (
    <div className="flex items-start gap-2 border-b border-[#f5f5f5] py-2 last:border-0">
      <span className="mt-0.5 shrink-0 text-[10px] text-amber-500">⚠</span>
      <span className="text-[11px] text-[#555]">{flag}</span>
    </div>
  );
}

export function isCompanyMapBoard(board: CohortBoardPayload): boolean {
  return Boolean(
    board.layout === 'revenue_map' ||
      (board.charts?.some((c) => c.kind === 'pie') && board.businesses?.nodes?.length),
  );
}

export function CompanyMapBoard({ board, memoSlot, onInvestigate }: Props) {
  const segments = useMemo(() => segmentsFromBoard(board), [board]);
  const totalQ2 = useMemo(() => segments.reduce((a, b) => a + b.q2, 0), [segments]);
  const customers = useMemo(() => customersFromBoard(board, totalQ2), [board, totalQ2]);
  const kpis = useMemo(() => kpisFromBoard(board), [board]);
  const capex = useMemo(() => capexFromBoard(board), [board]);
  const openFlags = useMemo(() => openFlagsFromBoard(board), [board]);
  const invLines = board.investigations?.lines ?? [];
  const defaultLine = useMemo(() => {
    const unclosed = invLines.find((l) => l.heat === 'unclosed' || l.heat === 'hole');
    return unclosed?.name ?? invLines[0]?.name ?? segments[0]?.name ?? null;
  }, [invLines, segments]);
  const [selectedLine, setSelectedLine] = useState<string | null>(defaultLine);
  const showTrend = board.object?.toUpperCase() === 'SPCX';
  const showOps = board.object?.toUpperCase() === 'SPCX';
  const showBacklog = board.object?.toUpperCase() === 'SPCX';
  const secUrl = board.object?.toUpperCase() === 'SPCX' ? SPCX_SEC_10Q : null;
  const subtitle = headerSubtitle(board);

  const selectSegment = (name: string) => {
    const hit = matchInvestigationLine(invLines, name);
    setSelectedLine(hit?.name ?? name);
  };

  if (!segments.length) {
    return (
      <div className="text-sm text-gray-500" data-company-map-board>
        Revenue map data is not available for this run.
        {memoSlot}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-company-map-board>
      <div className="flex flex-col gap-3 border-b border-[#e5e5e5] pb-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-[20px] leading-tight text-[#0d0d0d]">
            {board.object} — Revenue Map
          </h2>
          {subtitle ? <p className="mt-0.5 text-[12px] text-[#999]">{subtitle}</p> : null}
        </div>
        {secUrl ? (
          <a
            href={secUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 rounded-lg border border-[#e5e5e5] px-3 py-1.5 text-[12px] text-[#555] transition-colors hover:bg-[#f7f7f8]"
          >
            10-Q ↗
          </a>
        ) : null}
      </div>

      {board.headline ? (
        <p className="text-[13px] leading-6 text-[#555]">{board.headline}</p>
      ) : null}

      {kpis.length > 0 ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {kpis.map((k) => (
            <KPICard key={k.label} {...k} />
          ))}
        </div>
      ) : null}

      {board.drill || board.investigations?.stones?.length ? (
        <DrillFindings stones={board.investigations?.stones ?? []} drill={board.drill} />
      ) : null}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="flex flex-col gap-4 rounded-xl border border-[#e5e5e5] bg-white p-5">
          <div>
            <div className="text-[12px] font-semibold text-[#0d0d0d]">Revenue by segment</div>
            <div className="mt-0.5 text-[11px] text-[#999]">
              Q2 2026 · Total {fmtM(totalQ2)}
            </div>
          </div>
          <SegmentBar segments={segments} selected={selectedLine} onSelect={selectSegment} />
        </div>

        <div className="flex flex-col gap-4 rounded-xl border border-[#e5e5e5] bg-white p-5">
          <div>
            <div className="text-[12px] font-semibold text-[#0d0d0d]">Customer concentration</div>
            <div className="mt-0.5 text-[11px] text-[#999]">Top 2 customers · 10-Q Note 3</div>
          </div>
          <CustomerConc customers={customers} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {showTrend ? (
          <div className="flex flex-col gap-2">
            <div className="text-[12px] font-semibold text-[#0d0d0d]">Revenue trend ($B)</div>
            <div className="h-[180px] rounded-xl border border-[#e5e5e5] bg-[#fafafa] p-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={SPCX_TREND} barSize={18}>
                  <CartesianGrid vertical={false} stroke="#e5e5e5" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 9, fill: '#999', fontFamily: 'Inter' }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 9, fill: '#999', fontFamily: 'ui-monospace, monospace' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `$${v}B`}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => (
                      <ValueTooltip
                        active={active}
                        payload={payload as Array<{ value?: number | string | null }> | undefined}
                        label={label == null ? undefined : String(label)}
                        suffix="B"
                      />
                    )}
                    cursor={{ fill: '#f0f0f0' }}
                  />
                  <Bar dataKey="rev" radius={[3, 3, 0, 0]}>
                    {SPCX_TREND.map((_, i) => (
                      <Cell
                        key={i}
                        fill={i === SPCX_TREND.length - 1 ? '#0d0d0d' : '#d4d4d4'}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : null}

        {showOps ? (
          <div className="flex flex-col gap-2">
            <div className="text-[12px] font-semibold text-[#0d0d0d]">
              Operating income by segment
            </div>
            <div className="h-[180px] rounded-xl border border-[#e5e5e5] bg-[#fafafa] p-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={SPCX_SEGMENT_OPS} barSize={28} layout="vertical">
                  <CartesianGrid horizontal={false} stroke="#e5e5e5" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 9, fill: '#999', fontFamily: 'ui-monospace, monospace' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `$${v}M`}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 9, fill: '#555', fontFamily: 'Inter' }}
                    axisLine={false}
                    tickLine={false}
                    width={72}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => (
                      <ValueTooltip
                        active={active}
                        payload={payload as Array<{ value?: number | string | null }> | undefined}
                        label={label == null ? undefined : String(label)}
                      />
                    )}
                    cursor={{ fill: '#f5f5f5' }}
                  />
                  <Bar dataKey="ops" radius={[0, 3, 3, 0]}>
                    {SPCX_SEGMENT_OPS.map((s) => (
                      <Cell key={s.name} fill={s.ops >= 0 ? s.color : '#e94560'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : null}

        {capex.rows.length > 0 ? (
          <div className="flex flex-col gap-1 rounded-xl border border-[#e5e5e5] bg-[#fafafa] p-5">
            <div className="mb-2 text-[12px] font-semibold text-[#0d0d0d]">H1 capex vs OCF</div>
            <CapexBar
              rows={capex.rows}
              ocf={capex.ocf}
              showFundingNote={board.object?.toUpperCase() === 'SPCX'}
            />
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-2">
        <div className="text-[12px] font-semibold text-[#0d0d0d]">Segment detail</div>
        <div className="overflow-hidden rounded-xl border border-[#e5e5e5] bg-[#fafafa]">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="border-b border-[#e5e5e5]">
                <th className="px-5 py-2.5 text-left text-[10px] font-medium uppercase tracking-widest text-[#999]">
                  Segment
                </th>
                <th className="px-5 py-2.5 text-right text-[10px] font-medium uppercase tracking-widest text-[#999]">
                  Q2 Rev
                </th>
                <th className="px-5 py-2.5 text-right text-[10px] font-medium uppercase tracking-widest text-[#999]">
                  Mix
                </th>
                <th className="hidden px-5 py-2.5 text-left text-[10px] font-medium uppercase tracking-widest text-[#999] lg:table-cell">
                  Note
                </th>
              </tr>
            </thead>
            <tbody>
              {segments.map((s) => (
                <tr
                  key={s.name}
                  className="border-b border-[#f0f0f0] transition-colors last:border-0 hover:bg-white"
                >
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-2 w-2 shrink-0 rounded-sm"
                        style={{ backgroundColor: s.color }}
                      />
                      <span className="font-semibold text-[#0d0d0d]">{s.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-[#0d0d0d]">{fmtM(s.q2)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-[#e5e5e5]">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${s.pct}%`, backgroundColor: s.color }}
                        />
                      </div>
                      <span className="w-8 text-right font-mono text-[10px] text-[#999]">
                        {s.pct}%
                      </span>
                    </div>
                  </td>
                  <td className="hidden px-5 py-3 text-[11px] text-[#999] lg:table-cell">{s.note}</td>
                </tr>
              ))}
              <tr className="border-t border-[#e5e5e5] bg-white">
                <td className="px-5 py-3 font-semibold text-[#0d0d0d]">Total</td>
                <td className="px-5 py-3 text-right font-mono font-semibold text-[#0d0d0d]">
                  {fmtM(totalQ2)}
                </td>
                <td className="px-5 py-3 text-right font-mono text-[10px] text-[#999]">100%</td>
                <td className="hidden lg:table-cell" />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {invLines.length > 0 ? (
        <InvestigationDeck
          lines={invLines}
          howToUse={board.investigations?.how_to_use}
          selected={selectedLine}
          onSelect={setSelectedLine}
          onInvestigate={onInvestigate}
        />
      ) : null}

      {openFlags.length > 0 ? (
        <div className="flex flex-col gap-2">
          <div className="text-[12px] font-semibold text-[#0d0d0d]">
            Open — still not in the files
          </div>
          <div className="rounded-xl border border-amber-100 bg-amber-50 px-5 py-1">
            {openFlags.map((f) => (
              <FlagRow key={f} flag={f} />
            ))}
          </div>
        </div>
      ) : null}

      {board.overreach && board.overreach.length > 0 ? (
        <details className="rounded-xl border border-gray-200 px-4 py-2">
          <summary className="cursor-pointer text-sm font-medium text-gray-700">
            Inferred — do not use
          </summary>
          <ul className="mt-2 space-y-2 pb-2 text-sm text-gray-600">
            {board.overreach.map((item) => (
              <li key={item.claim}>
                <span className="font-medium text-gray-800">{item.claim}</span>
                {item.because ? ` — ${item.because}` : ''}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {showBacklog ? (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {SPCX_BACKLOG.map((b) => (
            <div key={b.label} className="rounded-xl border border-[#e5e5e5] bg-white px-5 py-4">
              <div className="text-[10px] font-medium uppercase tracking-widest text-[#999]">
                {b.label}
              </div>
              <div className="mt-1 text-[20px] leading-tight text-[#0d0d0d]">{b.value}</div>
              <div className="mt-0.5 text-[10px] text-[#bbb]">{b.sub}</div>
            </div>
          ))}
        </div>
      ) : null}

      {board.watch ? (
        <p className="text-sm leading-6 text-gray-600">
          <span className="font-semibold text-gray-800">Watch. </span>
          {board.watch}
        </p>
      ) : null}

      {memoSlot}
    </div>
  );
}
